import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  useTheme,
  IconButton,
  Chip,
  Alert,
  Paper,
  Skeleton,
  Fade,
  Zoom,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import { jsPDF } from "jspdf";
import { getVehicleById, deleteVehicle } from "../../../stores/VeihcleService";
import userService from "../../../stores/UserService";
import { useAuth } from "../../../contexts/AuthContext";
import DeleteConfirmationModal from "./DeleteModal";
import VehicleFormModal from "./VehicleFormModal";
import ProfileModal from "../../ProfileModal";

const VehicleDetailPage = () => {
  const theme = useTheme();
  const { vehicleId } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();

  const [vehicle, setVehicle] = useState(null);
  const [vehicleUser, setVehicleUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [feedbackAlert, setFeedbackAlert] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);

  const photoTypes = [
    { id: "front", label: "Frente", description: "Foto diretamente da frente do veículo" },
    { id: "diagonalFrontLeft", label: "Diagonal frontal - lado 1", description: "Foto diagonal frontal do lado do motorista" },
    { id: "diagonalRearLeft", label: "Diagonal traseira - lado 1", description: "Foto diagonal traseira do lado do motorista" },
    { id: "back", label: "Traseira", description: "Foto diretamente da traseira do veículo" },
    { id: "trunk", label: "Porta-malas", description: "Foto do porta-malas aberto" },
    { id: "diagonalRearRight", label: "Diagonal traseira - lado 2", description: "Foto diagonal traseira do lado do passageiro" },
    { id: "diagonalFrontRight", label: "Diagonal frontal - lado 2", description: "Foto diagonal frontal do lado do passageiro" },
    { id: "engine", label: "Motor", description: "Foto do compartimento do motor com o capô aberto" },
    { id: "Km", label: "Quilometragem", description: "Foto do contador de quilometros do veículo" },
    { id: "backSeat", label: "Banco traseiro", description: "Foto dos bancos traseiros do veículo" },
    { id: "damage1", label: "Avarias - Foto 1 (Opcional)", description: "Foto de avarias ou danos no veículo" },
    { id: "damage2", label: "Avarias - Foto 2 (Opcional)", description: "Foto de avarias ou danos no veículo" },
    { id: "damage3", label: "Avarias - Foto 3 (Opcional)", description: "Foto de avarias ou danos no veículo" }
  ];

  const vehicleItems = {
    hydraulic_steering: "Direção hidráulica",
    air_conditioning: "Ar condicionado",
    electric_windows: "Vidros elétricos",
    electric_locks: "Travas elétricas",
    electric_steering: "Direção elétrica",
    airbag: "Air bag",
    dual_airbag: "Air bag duplo",
    abs_brakes: "Freios ABS",
    ebd_brakes: "Freios EBD",
    traction_control: "Controle de tração",
    stability_control: "Controle de estabilidade",
    alarm: "Alarme",
    parking_sensor: "Sensor de estacionamento",
    reverse_sensor: "Sensor de ré",
    reverse_camera: "Câmera de ré",
    heater: "Ar quente",
    electric_seats: "Bancos elétricos",
    leather_seats: "Bancos em couro",
    electric_mirrors: "Retrovisores elétricos",
    height_adjustable_steering: "Volante com regulagem de altura",
    sunroof: "Teto solar",
    onboard_computer: "Computador de bordo",
    interface: "Interface",
    multimedia_center: "Central multimídia",
    rear_defroster: "Desembaçador traseiro",
    fog_lights: "Farol neblina",
    rear_wiper: "Limpador traseiro",
    green_windows: "Vidros verdes",
    alloy_wheels: "Rodas liga leve",
    turbo: "Turbo",
    tonneau_cover: "Lona marítima",
    bed_liner: "Protetor de caçamba",
    assisted_steering: "Direção assistida"
  };

  const ownershipTimeMap = {
    "less_than_6_months": "Menos de 6 meses",
    "6_to_12_months": "6 meses a 1 ano",
    "1_to_3_years": "1 a 3 anos",
    "3_to_5_years": "3 a 5 anos",
    "more_than_5_years": "Mais de 5 anos"
  };

  const conditionMap = {
    "excellent": "Excelente",
    "good": "Bom",
    "regular": "Regular",
    "bad": "Ruim"
  };

  useEffect(() => {
    fetchVehicle();
    checkUserRole();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleId]);

  useEffect(() => {
    console.log(vehicle)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicle]);

  const checkUserRole = async () => {
    if (currentUser) {
      try {
        const userData = await userService.getUserByUid(currentUser.uid);
        setIsAdmin(userData.isAdmin || false);
      } catch (error) {
        console.error("Erro ao verificar role do usuário:", error);
        setIsAdmin(false);
      }
    }
  };

  const fetchVehicle = async () => {
    try {
      setLoading(true);
      setError(null);
      const vehicleData = await getVehicleById(vehicleId);
      if (vehicleData) {
        setVehicle(vehicleData);
        // Buscar dados do usuário do veículo
        await fetchVehicleUser(vehicleData.userId);
      } else {
        setError("Veículo não encontrado");
      }
    } catch (error) {
      console.error("Erro ao buscar veículo:", error);
      setError("Erro ao carregar dados do veículo");
    } finally {
      setLoading(false);
    }
  };

  const fetchVehicleUser = async (userId) => {
    if (!userId) return;

    try {
      const userData = await userService.getUserByUid(userId);
      setVehicleUser(userData);
    } catch (error) {
      console.error("Erro ao buscar usuário do veículo:", error);
      setVehicleUser(null);
    }
  };

  const handleBack = () => {
    navigate("/dashboard");
  };

  const handleEdit = () => {
    setEditModalOpen(true);
  };

  const handleDelete = () => {
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!vehicle) return;

    // Verificar se o usuário tem permissão para deletar
    if (!isAdmin && currentUser?.uid !== vehicle.userId) {
      setFeedbackAlert({
        text: "Você não tem permissão para excluir este veículo",
        severity: "error"
      });
      setDeleteModalOpen(false);
      return;
    }

    try {
      await deleteVehicle(vehicle.userId, vehicle.id);
      setDeleteModalOpen(false);
      navigate("/dashboard");
    } catch (error) {
      console.error('Erro ao excluir veículo:', error);
      setFeedbackAlert({
        text: "Erro ao excluir veículo",
        severity: "error"
      });
    }
  };

  const handleSaveVehicle = () => {
    fetchVehicle();
    setEditModalOpen(false);
    setFeedbackAlert({
      text: "Veículo atualizado com sucesso!",
      severity: "success"
    });
  };

  const generateVehiclePDF = async (vehicle) => {
    const doc = new jsPDF();
    const mainColor = [24, 232, 219];
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    let y = 15;

    const checkPageBreak = (requiredHeight) => {
      if (y + requiredHeight > pageHeight - margin) {
        doc.addPage();
        y = 15;
        addHeader();
        return true;
      }
      return false;
    };

    const addHeader = async () => {
      const logoUrl = '/logoPDF.png';
      let logoBase64 = null;

      try {
        const response = await fetch(logoUrl);
        const blob = await response.blob();
        logoBase64 = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        });
      } catch (error) {
        console.error('Erro ao carregar o logo:', error);
      }

      if (logoBase64) {
        const logoWidth = 70;
        const logoHeight = 30;
        const pageWidth = doc.internal.pageSize.getWidth();
        const logoX = (pageWidth - logoWidth) / 2;

        doc.addImage({
          imageData: logoBase64,
          x: logoX,
          y: y,
          width: logoWidth,
          height: logoHeight,
          format: 'PNG'
        });

        y += logoHeight + 10;
      }

      doc.setFont("helvetica", "bold");
      doc.setFontSize(20);
      doc.setTextColor(...mainColor);
      doc.text("Ficha Técnica do Veículo", 105, y, { align: "center" });
      y += 10;

      doc.setFontSize(16);
      doc.setTextColor(33, 37, 41);
      const vehicleTitle = `${vehicle?.brand?.nome} ${vehicle.model} (${vehicle.year}/${vehicle.modelYear})`;

      const maxTitleWidth = 170; 
      const titleLines = doc.splitTextToSize(vehicleTitle, maxTitleWidth);

      const titleHeight = titleLines.length * 7; 


      titleLines.forEach((line, index) => {
        doc.text(line, 105, y + (index * 7), { align: "center" });
      });

      y += titleHeight + 10;

      doc.setDrawColor(...mainColor);
      doc.setLineWidth(0.5);
      doc.line(20, y, 190, y);
      y += 10;
    };

    await addHeader();

    checkPageBreak(30);

    doc.setFont("helvetica", "bold");
    doc.setTextColor(...mainColor);
    doc.text("Informações Básicas", 20, y);
    y += 8;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(12);
    doc.setTextColor(33, 37, 41);
    doc.text(`Placa: ${vehicle.plate}`, 20, y);
    doc.text(`Quilometragem: ${vehicle.mileage} km`, 105, y);
    y += 8;

    doc.text(
      `Tempo de posse: ${ownershipTimeMap[vehicle.ownershipTime] || vehicle.ownershipTime}`,
      20,
      y
    );

    y += 8;
    doc.text(`Chave Reserva: ${vehicle.hasSpareKey}`, 20, y);
    doc.text(`Manual de fábrica: ${vehicle.hasManual}`, 105, y);

    y += 10;
    doc.setDrawColor(...mainColor);
    doc.line(20, y, 190, y);
    y += 5;

    checkPageBreak(30);

    doc.setFont("helvetica", "bold");
    doc.setTextColor(...mainColor);
    doc.text("Estados do Veículo", 20, y);
    y += 8;

    doc.setFont("helvetica", "normal");
    doc.setTextColor(33, 37, 41);

    const getColorForCondition = (value) => {
      const val = (value || "").toLowerCase();
      if (val.includes("excellent")) return [46, 125, 50]; // Verde forte
      if (val.includes("good")) return [76, 175, 80]; // Verde claro
      if (val.includes("regular")) return [255, 152, 0]; // Amarelo/Laranja
      if (val.includes("bad")) return [211, 47, 47]; // Vermelho
      return [117, 117, 117]; // Cinza para não informado
    };

    const drawConditionItem = (label, value, x, y) => {
      const text = `${label}: ${conditionMap[value] || "Não informado"}`;
      const color = getColorForCondition(value || "");

      doc.setFillColor(...color);
      doc.circle(x, y - 1.5, 2, 'F');
      doc.text(text, x + 5, y);
    };

    drawConditionItem("Pintura", vehicle.paintCondition, 20, y);
    drawConditionItem("Interior", vehicle.interiorCondition, 20, y + 8);
    drawConditionItem("Pneus", vehicle.tiresCondition, 105, y);
    y += 18;

    doc.setDrawColor(...mainColor);
    doc.line(20, y, 190, y);
    y += 5;

    if (vehicle.items?.length > 0) {
      checkPageBreak(20);

      doc.setFont("helvetica", "bold");
      doc.setTextColor(...mainColor);
      doc.text("Itens do Veículo", 20, y);
      y += 8;

      doc.setFont("helvetica", "normal");
      doc.setTextColor(33, 37, 41);

      const drawItemSquare = (x, y) => {
        doc.setFillColor(0, 0, 0);
        doc.rect(x, y - 3, 3, 3, 'F');
      };

      const itemsList = vehicle.items.map(
        item => `${vehicleItems[item] || item}`
      );

      const colWidth = 85;
      const startX1 = 20;
      const startX2 = startX1 + colWidth;

      for (let i = 0; i < itemsList.length; i += 2) {
        if (y + 7 > pageHeight - margin) {
          doc.addPage();
          y = 15;
          addHeader();
          doc.setFont("helvetica", "bold");
          doc.setTextColor(...mainColor);
          doc.text("Itens do Veículo (continuação)", 20, y);
          y += 8;
          doc.setFont("helvetica", "normal");
          doc.setTextColor(33, 37, 41);
        }

        drawItemSquare(startX1, y);
        doc.text(itemsList[i], startX1 + 5, y);

        if (i + 1 < itemsList.length) {
          drawItemSquare(startX2, y);
          doc.text(itemsList[i + 1], startX2 + 5, y);
        }

        y += 7;
      }

      y += 5;
      doc.setDrawColor(...mainColor);
      doc.line(20, y, 190, y);
      y += 5;
    }
    if (vehicle.fipeValue || vehicle.askingPrice) {
      checkPageBreak(30);

      doc.setFont("helvetica", "bold");
      doc.setTextColor(...mainColor);
      doc.text("Cotações", 20, y);
      y += 8;

      doc.setFont("helvetica", "normal");
      doc.setTextColor(33, 37, 41);

      doc.setFillColor(240, 240, 240);
      doc.rect(20, y - 6, 170, 24, 'F');

      let currentY = y;

      if (vehicle.fipeValue) {
        const fipeFormatted = `Valor FIPE: R$ ${vehicle.fipeValue}`;
        doc.text(fipeFormatted, 25, currentY);
        currentY += 7;
      }

      if (vehicle.askingPrice) {
        const askingFormatted = `Pedida: R$ ${vehicle.askingPrice}`;
        doc.text(askingFormatted, 25, currentY);
        currentY += 7;
      }

      if (vehicle.fipeValue || vehicle.askingPrice) {
        doc.setFont("helvetica", "bold");
        doc.text("Aceitamos contra oferta!", 25, currentY);
        doc.setFont("helvetica", "normal");
        currentY += 7;
      }

      y = currentY + 1;
      doc.setDrawColor(...mainColor);
      doc.line(20, y, 190, y);
      y += 5;
    }

    if (vehicle.description) {
      checkPageBreak(20);

      doc.setFont("helvetica", "bold");
      doc.setTextColor(...mainColor);
      doc.text("Descrição Adicional", 20, y);
      y += 8;

      doc.setFont("helvetica", "normal");
      doc.setTextColor(33, 37, 41);
      const lines = doc.splitTextToSize(vehicle.description, 170);

      for (let i = 0; i < lines.length; i++) {
        if (y + 7 > pageHeight - margin) {
          doc.addPage();
          y = 15;
          addHeader();
          doc.setFont("helvetica", "bold");
          doc.setTextColor(...mainColor);
          doc.text("Descrição Adicional (continuação)", 20, y);
          y += 8;
          doc.setFont("helvetica", "normal");
          doc.setTextColor(33, 37, 41);
        }
        doc.text(lines[i], 20, y);
        y += 7;
      }
    }

    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.text(`Documento gerado em: ${new Date().toLocaleString("pt-BR")}`, 20, 280);
      doc.text(`Página ${i} de ${pageCount}`, 180, 280, { align: "right" });
    }

    doc.save(`Ficha_${vehicle?.brand?.nome}_${vehicle.model}_${vehicle.plate}.pdf`);
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
        {/* Skeleton do header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
          <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width={200} height={20} sx={{ mb: 1 }} />
            <Skeleton variant="text" width={300} height={32} />
          </Box>
        </Box>

        {/* Skeleton do conteúdo */}
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, lg: 8 }}>
            {[1, 2, 3].map((item) => (
              <Paper key={item} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                <Skeleton variant="text" width={200} height={28} sx={{ mb: 3 }} />
                <Grid container spacing={3}>
                  {[1, 2, 3, 4].map((subItem) => (
                    <Grid size={{ xs: 12, sm: 6 }} key={subItem}>
                      <Skeleton variant="rectangular" height={80} sx={{ borderRadius: 1 }} />
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            ))}
          </Grid>
          <Grid size={{ xs: 12, lg: 4 }}>
            {[1, 2, 3].map((item) => (
              <Paper key={item} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                <Skeleton variant="text" width={150} height={28} sx={{ mb: 3 }} />
                <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 1 }} />
              </Paper>
            ))}
          </Grid>
        </Grid>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button onClick={handleBack} startIcon={<ArrowBackIcon />}>
          Voltar ao Dashboard
        </Button>
      </Box>
    );
  }

  if (!vehicle) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Veículo não encontrado
        </Alert>
        <Button onClick={handleBack} startIcon={<ArrowBackIcon />}>
          Voltar ao Dashboard
        </Button>
      </Box>
    );
  }

  const date = vehicle.createdAt.toDate();
  const formattedDate = date.toLocaleDateString('pt-BR');


  const getConditionChipStyle = (value) => {

    console.log(value)
    const val = (value || "").toLowerCase();
    if (val.includes("excellent")) {
      return {
        backgroundColor: '#2E7D32', // Verde forte
        color: '#FFFFFF',
        fontWeight: 'bold'
      };
    }
    if (val.includes("good")) {
      return {
        backgroundColor: '#4CAF50', // Verde claro
        color: '#FFFFFF',
        fontWeight: 'bold'
      };
    }
    if (val.includes("regular")) {
      return {
        backgroundColor: '#FF9800', // Amarelo/Laranja
        color: '#FFFFFF',
        fontWeight: 'bold'
      };
    }
    if (val.includes("bad")) {
      return {
        backgroundColor: '#D32F2F', // Vermelho
        color: '#FFFFFF',
        fontWeight: 'bold'
      };
    }
    return {
      backgroundColor: '#757575', // Cinza
      color: '#FFFFFF',
      fontWeight: 'bold'
    };
  };

  return (
    <Fade in timeout={500}>
      <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
        {/* Header com breadcrumb */}
        <Zoom in timeout={600}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
            <IconButton
              onClick={handleBack}
              sx={{
                mr: 2,
                bgcolor: 'background.default',
                boxShadow: 1,
                '&:hover': { bgcolor: 'background.container' }
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                Dashboard / Meus Veículos
              </Typography>
              <Typography variant="h4" fontWeight="bold" color="text.primary">
                {vehicle?.brand?.nome} {vehicle.model} ({vehicle.year})
              </Typography>
            </Box>
          </Box>
        </Zoom>

        {/* Alert de feedback */}
        {feedbackAlert && (
          <Fade in timeout={300}>
            <Alert severity={feedbackAlert.severity} sx={{ mb: 3 }}>
              {feedbackAlert.text}
            </Alert>
          </Fade>
        )}

        {/* Conteúdo principal em grid responsivo */}
        <Grid container spacing={3}>
          {/* Coluna principal - Informações do veículo */}
          <Grid size={{ xs: 12, lg: 8 }}>
            <Fade in timeout={700}>
              <div>
                {/* Card de informações básicas */}
                <Paper sx={{ p: 3, mb: 3, borderRadius: 2, bgcolor: 'background.default' }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 3, color: 'text.primary' }}>
                    Informações Básicas
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Box sx={{ p: 2, bgcolor: 'background.container', borderRadius: 1 }}>
                        <Typography variant="subtitle2" color="text.primary" gutterBottom>
                          Marca
                        </Typography>
                        <Typography variant="body1" fontWeight="medium" color="text.primary">
                          {vehicle?.brand?.nome}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Box sx={{ p: 2, bgcolor: 'background.container', borderRadius: 1 }}>
                        <Typography variant="subtitle2" color="text.primary" gutterBottom>
                          Modelo
                        </Typography>
                        <Typography variant="body1" fontWeight="medium" color="text.primary">
                          {vehicle.model}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Box sx={{ p: 2, bgcolor: 'background.container', borderRadius: 1 }}>
                        <Typography variant="subtitle2" color="text.primary" gutterBottom>
                          Ano (Fabricação/Modelo)
                        </Typography>
                        <Typography variant="body1" fontWeight="medium" color="text.primary">
                          {vehicle.year + "/" + vehicle.modelYear}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Box sx={{ p: 2, bgcolor: 'background.container', borderRadius: 1 }}>
                        <Typography variant="subtitle2" color="text.primary" gutterBottom>
                          Placa
                        </Typography>
                        <Typography variant="body1" fontWeight="medium" sx={{
                          fontFamily: 'monospace',
                          fontSize: '1.1rem',
                          letterSpacing: '0.1em',
                          color: 'text.primary'
                        }}>
                          {vehicle.plate}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Box sx={{ p: 2, bgcolor: 'background.container', borderRadius: 1 }}>
                        <Typography variant="subtitle2" color="text.primary" gutterBottom>
                          Quilometragem
                        </Typography>
                        <Typography variant="body1" fontWeight="medium" color="text.primary">
                          {vehicle.mileage.toLocaleString('pt-BR')} km
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Box sx={{ p: 2, bgcolor: 'background.container', borderRadius: 1 }}>
                        <Typography variant="subtitle2" color="text.primary" gutterBottom>
                          Tempo de posse
                        </Typography>
                        <Typography variant="body1" fontWeight="medium" color="text.primary">
                          {ownershipTimeMap[vehicle.ownershipTime] || vehicle.ownershipTime}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>

                {/* Card de estados do veículo */}
                <Paper sx={{ p: 3, mb: 3, borderRadius: 2, bgcolor: 'background.default' }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 3, color: 'text.primary' }}>
                    Estados do Veículo
                  </Typography>
                  <Grid container spacing={2}>
                    {[
                      { key: 'paintCondition', label: 'Pintura', icon: '🎨' },
                      { key: 'mechanicalCondition', label: 'Mecânica', icon: '🔧' },
                      { key: 'interiorCondition', label: 'Interior', icon: '💺' },
                      { key: 'tiresCondition', label: 'Pneus', icon: '🛞' }
                    ].map((item) => (
                      <Grid size={{ xs: 12, sm: 6, md: 3 }} key={item.key}>
                        <Box sx={{
                          p: 2,
                          bgcolor: 'background.container',
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: 'divider',
                          textAlign: 'center',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            boxShadow: 2,
                            transform: 'translateY(-2px)'
                          }
                        }}>
                          <Typography variant="h4" sx={{ mb: 1 }}>
                            {item.icon}
                          </Typography>
                          <Typography variant="subtitle2" color="text.primary" gutterBottom>
                            {item.label}
                          </Typography>
                          <Chip
                            label={conditionMap[vehicle[item.key]] || vehicle[item.key] || "Não informado"}
                            size="small"
                            style={getConditionChipStyle(vehicle[item.key])}
                            variant="filled"
                          />
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Paper>

                {/* Card de cotações */}
                {isAdmin && (
                  <Paper sx={{ p: 3, mb: 3, borderRadius: 2, bgcolor: 'background.default' }}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 3, color: 'text.primary' }}>
                      Cotações
                    </Typography>
                    <Grid container spacing={3}>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Box sx={{
                          p: 3,
                          bgcolor: 'success.light',
                          borderRadius: 2,
                          textAlign: 'center',
                          color: 'success.contrastText'
                        }}>
                          <Typography variant="h4" sx={{ mb: 1 }}>
                            💰
                          </Typography>
                          <Typography variant="subtitle2" gutterBottom>
                            Valor FIPE
                          </Typography>
                          <Typography variant="h6" fontWeight="bold">
                            {vehicle.fipeValue || "Não informado"}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <Box sx={{
                          p: 3,
                          bgcolor: 'primary.light',
                          borderRadius: 2,
                          textAlign: 'center',
                          color: 'primary.contrastText'
                        }}>
                          <Typography variant="h4" sx={{ mb: 1 }}>
                            🎯
                          </Typography>
                          <Typography variant="subtitle2" gutterBottom>
                            Valor Pedido
                          </Typography>
                          <Typography variant="h6" fontWeight="bold">
                            {vehicle.askingPrice || "Não informado"}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Paper>
                )}

                {/* Card de descrição */}
                {vehicle.description && (
                  <Paper sx={{ p: 3, mb: 3, borderRadius: 2, bgcolor: 'background.default' }}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 3, color: 'text.primary' }}>
                      Descrição
                    </Typography>
                    <Typography variant="body1" sx={{
                      whiteSpace: 'pre-line',
                      lineHeight: 1.6,
                      color: 'text.primary'
                    }}>
                      {vehicle.description}
                    </Typography>
                  </Paper>
                )}

                {/* Card de itens do veículo */}
                {vehicle.items?.length > 0 && (
                  <Paper sx={{ p: 3, mb: 3, borderRadius: 2, bgcolor: 'background.default' }}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 3, color: 'text.primary' }}>
                      Itens do Veículo
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {vehicle.items.map(item => (
                        <Chip
                          key={item}
                          label={vehicleItems[item]}
                          size="medium"
                          style={{
                            backgroundColor: theme.palette.primary.main,
                            color: theme.palette.primary.contrastText,
                            fontWeight: 500,
                            '&:hover': {
                              backgroundColor: theme.palette.primary.dark,
                            }
                          }}
                        />
                      ))}
                    </Box>
                  </Paper>
                )}
              </div>
            </Fade>
          </Grid>

          {/* Sidebar - Informações adicionais */}
          <Grid size={{ xs: 12, lg: 4 }}>
            {/* Card do proprietário */}
            {vehicleUser && (
              <Paper sx={{ p: 3, mb: 3, borderRadius: 2, bgcolor: 'background.default' }}>
                <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 3, color: 'text.primary' }}>
                  Proprietário
                </Typography>
                <Box sx={{
                  p: 2,
                  bgcolor: 'background.container',
                  borderRadius: 1,
                  textAlign: 'center'
                }}>
                  <Typography variant="h4" sx={{ mb: 2 }}>
                    👤
                  </Typography>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => { setProfileOpen(true); }}
                    sx={{
                      color: theme.palette.text.primary,
                      borderColor: theme.palette.divider,
                      '&:hover': {
                        borderColor: theme.palette.primary.main,
                        color: theme.palette.primary.main
                      }
                    }}
                  >
                    {vehicleUser?.name}
                  </Button>
                </Box>
              </Paper>
            )}

            {/* Card de informações adicionais */}
            <Paper sx={{ p: 3, mb: 3, borderRadius: 2, bgcolor: 'background.default' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 3, color: 'text.primary' }}>
                Informações Adicionais
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 6 }}>
                  <Box sx={{ textAlign: 'center', p: 2 }}>
                    <Typography variant="h4" sx={{ mb: 1 }}>
                      🔑
                    </Typography>
                    <Typography variant="subtitle2" color="text.primary" gutterBottom>
                      Chave Reserva
                    </Typography>
                    <Chip
                      label={vehicle.hasSpareKey || "Não informado"}
                      size="small"
                      color={vehicle.hasSpareKey === "Sim" ? "success" : "default"}
                    />
                  </Box>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Box sx={{ textAlign: 'center', p: 2 }}>
                    <Typography variant="h4" sx={{ mb: 1 }}>
                      📖
                    </Typography>
                    <Typography variant="subtitle2" color="text.primary" gutterBottom>
                      Manual
                    </Typography>
                    <Chip
                      label={vehicle.hasManual || "Não informado"}
                      size="small"
                      color={vehicle.hasManual === "Sim" ? "success" : "default"}
                    />
                  </Box>
                </Grid>
              </Grid>
              <Box sx={{ mt: 2, p: 2, bgcolor: 'background.container', borderRadius: 1 }}>
                <Typography variant="body2" color="text.primary" align="center">
                  Adicionado em {formattedDate}
                </Typography>
              </Box>
            </Paper>

            {/* Card de ações rápidas */}
            <Paper sx={{ p: 3, borderRadius: 2, bgcolor: 'background.default' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 3, color: 'text.primary' }}>
                Ações Rápidas
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {/* Botão Editar - só para admin ou proprietário */}
                {(isAdmin || currentUser?.uid === vehicle?.userId) && (
                  <Button
                    variant="contained"
                    startIcon={<EditIcon />}
                    onClick={handleEdit}
                    fullWidth
                    sx={{
                      bgcolor: theme.palette.primary.main,
                      color: theme.palette.primary.contrastText,
                      fontWeight: "bold",
                      "&:hover": {
                        bgcolor: theme.palette.primary.dark,
                      },
                    }}
                  >
                    Editar Veículo
                  </Button>
                )}

                {/* Botão Gerar PDF - só para admin */}
                {isAdmin && (
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={() => generateVehiclePDF(vehicle)}
                    fullWidth
                    sx={{
                      borderColor: theme.palette.primary.main,
                      color: theme.palette.primary.main,
                      "&:hover": {
                        borderColor: theme.palette.primary.dark,
                        bgcolor: "rgba(24, 232, 219, 0.04)",
                      },
                    }}
                  >
                    Gerar PDF
                  </Button>
                )}

                {/* Botão Excluir - só para admin ou proprietário */}
                {(isAdmin || currentUser?.uid === vehicle?.userId) && (
                  <Button
                    variant="outlined"
                    startIcon={<DeleteIcon />}
                    onClick={handleDelete}
                    fullWidth
                    sx={{
                      borderColor: theme.palette.error.main,
                      color: theme.palette.error.main,
                      "&:hover": {
                        borderColor: theme.palette.error.dark,
                        bgcolor: "rgba(244, 67, 54, 0.04)",
                      },
                    }}
                  >
                    Excluir Veículo
                  </Button>
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Seção de fotos */}
        <Box sx={{ mt: 4 }}>
          <Paper sx={{ p: 3, borderRadius: 2, bgcolor: 'background.default' }}>
            <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mb: 3, color: 'text.primary' }}>
              Galeria de Fotos
            </Typography>

            {/* Foto principal */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 2, color: 'text.primary' }}>
                Foto Principal - Frente
              </Typography>
              <Box sx={{ position: 'relative' }}>
                <Box
                  component="img"
                  src={vehicle.photos.front}
                  alt={`${vehicle?.brand?.nome} ${vehicle.model} - Frente`}
                  sx={{
                    width: "100%",
                    maxHeight: 400,
                    objectFit: "contain",
                    borderRadius: 2,
                    boxShadow: 3,
                    transition: 'transform 0.3s ease',
                    cursor: 'pointer',
                    '&:hover': {
                      transform: 'scale(1.02)'
                    }
                  }}
                  onClick={() => window.open(vehicle.photos.front, '_blank')}
                />
                <IconButton
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    bgcolor: 'rgba(0,0,0,0.6)',
                    color: 'white',
                    '&:hover': {
                      bgcolor: 'rgba(0,0,0,0.8)',
                    }
                  }}
                  onClick={() => window.open(vehicle.photos.front, '_blank')}
                >
                  <VisibilityIcon />
                </IconButton>
              </Box>
            </Box>

            {/* Galeria de fotos secundárias */}
            <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 2, color: 'text.primary' }}>
              Outras Fotos
            </Typography>
            <Grid container spacing={2}>
              {photoTypes.slice(1)
                .filter(type => vehicle.photos[type.id] && vehicle.photos[type.id] !== "")
                .map((type) => (
                  <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={type.id}>
                    <Box sx={{
                      position: 'relative',
                      height: '100%',
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 2,
                      overflow: 'hidden',
                      transition: 'all 0.3s ease',
                      cursor: 'pointer',
                      '&:hover': {
                        boxShadow: 4,
                        transform: 'translateY(-4px)',
                      }
                    }}
                      onClick={() => window.open(vehicle.photos[type.id], '_blank')}
                    >
                      <Box
                        component="img"
                        src={vehicle.photos[type.id]}
                        alt={`${vehicle?.brand?.nome} ${vehicle.model} - ${type.label}`}
                        sx={{
                          width: "100%",
                          height: 200,
                          objectFit: "cover",
                        }}
                      />
                      <IconButton
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          bgcolor: 'rgba(0,0,0,0.6)',
                          color: 'white',
                          '&:hover': {
                            bgcolor: 'rgba(0,0,0,0.8)',
                          }
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(vehicle.photos[type.id], '_blank');
                        }}
                      >
                        <VisibilityIcon />
                      </IconButton>
                      <Box sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        background: 'linear-gradient(transparent, rgba(0,0,0,0.8))',
                        color: 'white',
                        p: 2,
                      }}>
                        <Typography variant="body2" fontWeight="medium">
                          {type.label}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                ))}
            </Grid>
          </Paper>
        </Box>

        {/* Modais */}
        <DeleteConfirmationModal
          open={deleteModalOpen}
          onClose={() => setDeleteModalOpen(false)}
          onConfirm={confirmDelete}
          vehicleName={
            vehicle ? `${vehicle.brand.nome} ${vehicle.model} (${vehicle.year})` : ""
          }
        />

        <VehicleFormModal
          open={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          onSave={handleSaveVehicle}
          onFeedback={setFeedbackAlert}
          vehicle={vehicle}
          isEditing={true}
          userId={vehicle.userId}
          view={true}
        />

        <ProfileModal
          open={profileOpen}
          onClose={() => { setProfileOpen(false); }}
          userProvider={{ ...vehicleUser, uid: vehicleUser?.id }}
          mode={'view'}
        />
      </Box>
    </Fade>
  );
};

export default VehicleDetailPage; 